import { Trans, useLingui } from '@lingui/react/macro';
import { dataContext, FSRef, Project } from '@sdk';
import { useProject } from '@sdk/react/hooks';
import { useAgentContext } from '@src/components/agent-layout/agent-layout';
import { iconForType } from '@src/components/graph-view/icons/iconRegistry';
import {
  canonicalPath,
  NewProjectDialog,
  projectListToSelectorItems,
  ProjectSelectorModal,
  useEnsureProject,
} from '@src/components/project-selector';
import { Button } from '@src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@src/components/ui/dialog';
import { Input } from '@src/components/ui/input';
import { useAllProjects } from '@src/hooks/use-all-projects';
import { notify } from '@src/notifications';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ScopeSelection, type Scope } from './ScopeSelection';
import { getDescriptor, creationMounts, type QuickCreateDescriptor } from './registry';
import { useProjectSnapshot } from './useProjectSnapshot';
import { useActiveWorkspace } from '@src/hooks/use-workspaces';

interface QuickCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Descriptor `type` (e.g. 'skill', 'subagent'). Ignored when null. */
  type: string | null;
  /** Initial creation scope; undefined follows the active project. */
  project?: Project | null;
}

function projectPrefix(project: Project | null): string | null {
  if (!project) return null;
  return project.fs_storage_mount_path ?? null;
}

function defaultPathFor(scope: Scope, descriptor: QuickCreateDescriptor, mount: string): string {
  if (scope.kind === 'folder') return scope.folderPath ?? '';
  const sub = mount || creationMounts(descriptor.type)[0] || '';
  const prefix = scope.kind === 'user' ? dataContext.bootstrapInfo?.desktop_info?.paths?.home : projectPrefix(scope.project);
  return prefix && sub ? `${prefix.replace(/[\\/]+$/, '')}/${sub}` : '';
}

function initialScope(project: Project | null): Scope {
  return {
    kind: project ? 'project' : 'user',
    project,
    folderPath: null,
  };
}

export function QuickCreateDialog({ open, onOpenChange, type, project: explicitProject }: QuickCreateDialogProps) {
  const { t } = useLingui();
  const descriptor = type ? getDescriptor(type) : undefined;
  const { navigation } = useDockNavigation();
  const { project: activeProject } = useProject();
  const project = explicitProject === undefined ? activeProject : explicitProject;
  const { computeNode } = useAgentContext();
  // New projects go in the ACTIVE workspace's folder; the picker lists its projects.
  const { root: defaultWorkspacePath, scopeId } = useActiveWorkspace();
  const { projects: allProjects, isLoading: isLoadingProjects } = useAllProjects({ enabled: open, workspace: scopeId });
  const { restore, commit } = useProjectSnapshot(open);

  const [name, setName] = useState('');
  const [scope, setScope] = useState<Scope>(() => initialScope(project ?? null));
  const [mount, setMount] = useState('');
  const [path, setPath] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const nameRef = useRef<HTMLInputElement | null>(null);


  // Reset form whenever the dialog opens for a new type
  useEffect(() => {
    if (open && descriptor) {
      const next = initialScope(project ?? null);
      setName('');
      setScope(next);
      setMount(creationMounts(descriptor.type)[0] ?? '');
      setPath(defaultPathFor(next, descriptor, creationMounts(descriptor.type)[0] ?? ''));
      setIsSubmitting(false);
      requestAnimationFrame(() => nameRef.current?.focus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, descriptor?.type]);

  // When the user's currently-active project changes while scope === 'project',
  // mirror it into the scope and re-seed the path (only if the user hasn't
  // hand-edited the path away from the previous default).
  useEffect(() => {
    if (!open || !descriptor) return;
    if (scope.kind !== 'project') return;
    if (scope.project?.id === project?.id) return;
    const next: Scope = { ...scope, project: project ?? null };
    setScope(next);
    setPath((current) =>
      current === defaultPathFor(scope, descriptor, mount) ? defaultPathFor(next, descriptor, mount) : current,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const handleScopeChange = useCallback(
    (next: Scope) => {
      if (!descriptor) return;
      setScope(next);
      // Seeding the path on chip change is part of the spec: chip = default-setter.
      setPath(defaultPathFor(next, descriptor, mount));
    },
    [descriptor, mount],
  );

  const handleMountChange = useCallback(
    (next: string) => {
      if (!descriptor) return;
      setMount(next);
      setPath(defaultPathFor(scope, descriptor, next));
    },
    [descriptor, scope],
  );

  const projectItems = useMemo(() => projectListToSelectorItems(allProjects), [allProjects]);

  const currentProjectKey = useMemo(
    () => canonicalPath(scope.project?.fs_storage_mount_path ?? ''),
    [scope.project?.fs_storage_mount_path],
  );

  const ensureProject = useEnsureProject();

  const applyProjectScope = useCallback(
    (selectedProject: Project) => {
      if (!descriptor) return;
      const next: Scope = { kind: 'project', project: selectedProject, folderPath: null };
      setScope(next);
      setPath(defaultPathFor(next, descriptor, mount));
    },
    [descriptor, mount],
  );

  const handleProjectPick = useCallback(
    async (pickedKey: string) => {
      const picked = allProjects.find((p) => !!p.cwd && canonicalPath(p.cwd) === pickedKey);
      if (!picked?.cwd) return;
      const selectedProject = await ensureProject(picked.cwd, { select: false });
      applyProjectScope(selectedProject);
    },
    [allProjects, applyProjectScope, ensureProject],
  );

  const handleCreateProject = useCallback(
    async (rawName: string, rawParent: string) => {
      const cleanName = rawName.trim();
      const cleanParent = rawParent.trim().replace(/\\/g, '/').replace(/\/+$/, '');
      if (!cleanName || !cleanParent) throw new Error('Name and parent folder required');
      const selectedProject = await ensureProject(`${cleanParent}/${cleanName}`, { select: false });
      applyProjectScope(selectedProject);
    },
    [applyProjectScope, ensureProject],
  );

  const handlePickFolder = useCallback(async (): Promise<string | null> => {
    if (!computeNode) {
      notify.error({ title: t`No compute node available` });
      return null;
    }
    try {
      return await computeNode.openPathDialog();
    } catch (err) {
      console.error('[QuickCreateDialog] Folder picker failed:', err);
      notify.error({ title: t`Failed to open folder picker` });
      return null;
    }
  }, [computeNode, t]);

  const handleCreate = useCallback(async () => {
    if (!descriptor || !name.trim() || !path.trim() || isSubmitting || !computeNode?.typeId) return;
    if (descriptor.allowedScopes && !descriptor.allowedScopes.includes(scope.kind)) return;
    setIsSubmitting(true);
    try {
      const selectedProject = scope.kind === 'project' ? scope.project : null;
      const res = await descriptor.create({
        project: selectedProject,
        name,
        scope: scope.kind,
        destination: new FSRef(path, computeNode.typeId, 'folder').toJSON(),
      });
      notify.success({ title: t(res.toastTitle) });
      commit();
      if (res.pointer) navigation.openDock(res.pointer);
      onOpenChange(false);
    } catch (err) {
      console.error('[QuickCreateDialog] Create failed:', err);
      notify.error({ title: t`Failed to create` });
    } finally {
      setIsSubmitting(false);
    }
  }, [descriptor, name, path, scope, computeNode, isSubmitting, commit, navigation, onOpenChange, t]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) void restore();
      onOpenChange(next);
    },
    [onOpenChange, restore],
  );

  if (!descriptor) return null;
  // Backend type registry owns the glyph (TypeInfo.icon).
  const Icon = iconForType(descriptor.type);
  const scopeAllowed = !descriptor.allowedScopes || descriptor.allowedScopes.includes(scope.kind);
  const canCreate = !!computeNode?.typeId && !!name.trim() && !!path.trim() && scopeAllowed && !isSubmitting;

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="h-4 w-4" />
              <Trans>New {t(descriptor.label)}</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>Create a new {t(descriptor.label).toLowerCase()}.</Trans>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3 pt-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">{t`Name`}</label>
              <Input
                ref={nameRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t`New ${t(descriptor.label).toLowerCase()} name`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canCreate) void handleCreate();
                }}
                autoFocus
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">{t`Scope`}</label>
              <ScopeSelection
                scope={scope}
                onScopeChange={handleScopeChange}
                mount={mount}
                onMountChange={handleMountChange}
                path={path}
                onPathChange={setPath}
                onPickFolder={handlePickFolder}
                onOpenProjectPicker={() => setProjectPickerOpen(true)}
                mounts={creationMounts(descriptor.type)}
                allowedScopes={descriptor.allowedScopes}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
              {t`Cancel`}
            </Button>
            <Button onClick={() => void handleCreate()} disabled={!canCreate}>
              {isSubmitting ? <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              {t`Create`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ProjectSelectorModal
        open={projectPickerOpen}
        onOpenChange={setProjectPickerOpen}
        projects={projectItems}
        selectedId={currentProjectKey || null}
        onSelect={(id) => void handleProjectPick(id)}
        isLoading={isLoadingProjects}
        onCreateNew={() => {
          setProjectPickerOpen(false);
          setNewProjectOpen(true);
        }}
      />

      <NewProjectDialog
        open={newProjectOpen}
        onOpenChange={setNewProjectOpen}
        defaultParentFolder={defaultWorkspacePath}
        onPickFolder={handlePickFolder}
        onCreate={handleCreateProject}
      />
    </>
  );
}
